import * as React from 'react';
import moment from 'moment';
import Field from '../../Field/Field';
import NewSelect from '../../NewSelect/NewSelect';
import HStack from '../../HStack/HStack';
import NewDatePicker from '../../NewDatePicker/NewDatePicker';
import { FilterComponentProps } from '../types';

export interface DateRangeFilterProps<T> extends FilterComponentProps<T> {
  /** Start date value attribute name, default `start` */
  startKey?: keyof T;
  /** End date value attribute name, default `start` */
  endKey?: keyof T;
  /** Date format, default `MM/DD/YYYY` */
  format?: string;
}

export const DateRangeFilter = <T extends Record<string, string>>({
  startKey = 'start' as keyof T,
  endKey = 'end' as keyof T,
  format = 'MM/DD/YYYY',
  ...props
}: DateRangeFilterProps<T>) => {
  if (props.setValue === undefined) {
    throw new Error('DateRangeFilter: missing required props - did you forget to spread {...props}?');
  }
  const [selection, setSelection] = React.useState('');
  const start = props.value[startKey] || '';
  const end = props.value[endKey] || '';

  return (
    <>
      <HStack spacing="16" align="distributed">
        <Field isStacked density="high" label="From">
          <NewDatePicker
            aria-label="Start date"
            width="full"
            value={start}
            onChange={(_, rv) => {
              props.setValue({ [startKey]: rv.value, [endKey]: end } as T);
              setSelection('');
            }}
            calendar={props => (
              <NewDatePicker.Calendar
                {...props}
                disableDates={date => {
                  if (!end) {
                    return false;
                  }
                  return moment(date, format).isAfter(moment(end, format));
                }}
              />
            )}
          />
        </Field>
        <Field isStacked density="high" label="To">
          <NewDatePicker
            aria-label="End date"
            align="right"
            width="full"
            value={end}
            onChange={(_, rv) => {
              props.setValue({ [startKey]: start, [endKey]: rv.value } as T);
              setSelection('');
            }}
            calendar={props => (
              <NewDatePicker.Calendar
                {...props}
                disableDates={date => {
                  if (!start) {
                    return false;
                  }
                  return moment(date, format).isBefore(moment(start, format));
                }}
              />
            )}
          />
        </Field>
      </HStack>
      <Field isStacked label="Date range">
        <NewSelect
          value={selection}
          width="full"
          onChange={e => {
            const value = e.target.value;
            setSelection(value);

            switch (value) {
              case 'last_week':
                props.setValue({
                  [startKey]: moment().subtract(1, 'week').format(format),
                  [endKey]: moment().format(format),
                } as T);
                break;
              case 'last_30_days':
                props.setValue({
                  [startKey]: moment().subtract(30, 'days').format(format),
                  [endKey]: moment().format(format),
                } as T);
                break;
              case 'this_month':
                props.setValue({
                  [startKey]: moment().startOf('month').format(format),
                  [endKey]: moment().format(format),
                } as T);
                break;
              case 'last_month':
                props.setValue({
                  [startKey]: moment().subtract(1, 'month').startOf('month').format(format),
                  [endKey]: moment().subtract(1, 'month').endOf('month').format(format),
                } as T);
                break;
              case 'this_quarter':
                props.setValue({
                  [startKey]: moment().startOf('quarter').format(format),
                  [endKey]: moment().format(format),
                } as T);
                break;
              case 'last_quarter':
                props.setValue({
                  [startKey]: moment().subtract(1, 'quarter').startOf('quarter').format(format),
                  [endKey]: moment().subtract(1, 'quarter').endOf('quarter').format(format),
                } as T);
                break;
              case 'last_6_months':
                props.setValue({
                  [startKey]: moment().subtract(6, 'months').format(format),
                  [endKey]: moment().format(format),
                } as T);
                break;
              case 'this_year':
                props.setValue({
                  [startKey]: moment().startOf('year').format(format),
                  [endKey]: moment().format(format),
                } as T);
                break;
              case 'last_year':
                props.setValue({
                  [startKey]: moment().subtract(1, 'year').startOf('year').format(format),
                  [endKey]: moment().subtract(1, 'year').endOf('year').format(format),
                } as T);
                break;
              default:
                // selecting "Custom" does not set any value
                break;
            }
          }}
        >
          <NewSelect.Option value="">Custom</NewSelect.Option>
          <NewSelect.Option value="last_week">Last week</NewSelect.Option>
          <NewSelect.Option value="last_30_days">Last 30 days</NewSelect.Option>
          <NewSelect.Option value="this_month">This month</NewSelect.Option>
          <NewSelect.Option value="last_month">Last month</NewSelect.Option>
          <NewSelect.Option value="this_quarter">This quarter</NewSelect.Option>
          <NewSelect.Option value="last_quarter">Last quarter</NewSelect.Option>
          <NewSelect.Option value="last_6_months">Last 6 months</NewSelect.Option>
          <NewSelect.Option value="this_year">This year</NewSelect.Option>
          <NewSelect.Option value="last_year">Last year</NewSelect.Option>
        </NewSelect>
      </Field>
    </>
  );
};

DateRangeFilter.displayName = 'GlobalFilter.DateRangeFilter';
